import type { AdminRouteName } from '@lastrouter/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { api, ApiError, type ApiArgs, type ApiResponse } from './client.js';

export function queryKey<N extends AdminRouteName>(name: N, args?: ApiArgs<N>): readonly unknown[] {
  return args && Object.keys(args).length ? [name, args] : [name];
}

export function useApiQuery<N extends AdminRouteName>(
  name: N,
  args?: ApiArgs<N>,
  options: Omit<UseQueryOptions<ApiResponse<N>, ApiError>, 'queryKey' | 'queryFn'> = {},
) {
  return useQuery<ApiResponse<N>, ApiError>({
    queryKey: queryKey(name, args),
    queryFn: () => api(name, args),
    ...options,
  });
}

/** Mutação que, ao terminar, recarrega as consultas listadas (o que a tela mostra depois da mudança). */
export function useApiMutation<N extends AdminRouteName>(name: N, invalidate: AdminRouteName[] = []) {
  const qc = useQueryClient();
  return useMutation<ApiResponse<N>, ApiError, ApiArgs<N>>({
    mutationFn: (args) => api(name, args),
    onSettled: async () => {
      await Promise.all(invalidate.map((n) => qc.invalidateQueries({ queryKey: [n] })));
    },
  });
}

/** Mensagem para a pessoa: o texto do serviço quando há, senão um genérico por situação. */
export function errorMessage(t: TFunction, e: unknown): string {
  if (!(e instanceof ApiError)) return t('errors.generic');
  if (e.code === 'network') return t('errors.network');
  if (e.code === 'invalid_response') return t('errors.invalidResponse');
  if (e.status === 401) return t('errors.session');
  if (e.status === 403 && e.code !== 'csrf') return e.message || t('errors.forbidden');
  if (e.status === 409 && e.code === 'conflict') return t('errors.conflict');
  return e.message || t('errors.generic');
}
